import { useCallback, useEffect, useMemo, useState } from 'react'
import { SubTask } from '../../types'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentFormId,
  selectCurrentQuestionIndex,
  selectFormSubmission,
  selectQuestions,
} from '../../../../modules/formQuestion/selectors'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { theseus } from '../../../../api/theseus'
import {
  setFormId,
  setFormQuestions,
  setFormSubmission,
  setNextQuestion,
  setPreviousQuestion,
} from '../../../../modules/formQuestion/slice'
import { toast } from 'react-toastify'
import { ProgressBar, ProgressContainer, TaskContainer } from './styles'
import {
  ModalText,
  ModalTitle,
  stopPropagation,
} from '../../../EmbeddableModal'
import { Box } from '../../../UI/Box'
import { ThemedButton } from '../../../ThemedButton'
import Question from './Question'
import { Info } from '../../styles'
import ConfettiExplosion from 'react-confetti-explosion'
import { Form } from './types'
import styled from 'styled-components'
import { wrapRewardDisplay } from '../../../../page_components/Home/useUpcomingProjects'
import { defaultTransition } from '../../../../theme'
import {
  useLazyGetFormByTaskIdAndFormMissionIdQuery,
  useLazyGetUserFormSubmissionByTaskIdQuery,
} from '../../../../modules/formQuestion/api'
import useIsLoggedIn from '../../../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../../Library/Buttons/ConnectWalletButton'
import CustomMarkdown from '../../../Library/CustomMarkdown'

const DescriptionCont = styled.span`
  a {
    color: ${({ theme }) => theme.colors.brandPrimary};
    border-bottom: 1px solid;
    cursor: pointer;
    opacity: 1;
    line-height: 1.5;
    ${defaultTransition}

    :hover {
      opacity: 0.8;
    }
  }
`

export const Wrapper = styled(Box)`
  position: relative;
  z-index: 12;
  padding: 2rem 0 0 0;
  button {
    max-width: 300px;
  }
`

export type Choice = {
  id: string
  text: string
  questionId: string
}

type TextQuestion = {
  type: 'text'
}

type MultipleChoiceQuestion = {
  type: 'multiple_choice'
  isMultipleSelection: boolean
  choices: Array<Choice>
}

export type QuestionType = {
  id: string
  title: string
  isRequired: boolean
} & (TextQuestion | MultipleChoiceQuestion)

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`

const SharedFormTaskCard = ({
  subtask,
  taskId,
  reward,
  updateTaskSubmissions,
  isDrop = false,
  isEthRaise = false,
  maxReward,
}: {
  subtask: SubTask
  taskId: number
  reward: number
  isDrop?: boolean
  updateTaskSubmissions: () => Promise<unknown>
  isEthRaise?: boolean
  maxReward?: number
}) => {
  const [formData, setFormData] = useState<Form | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [skippedQuestionIds, setSkippedQuestionIds] = useState<string[]>([])
  const [hasVisitedPrequisiteLink, setHasVisitedPrerequisiteLink] =
    useState(false)
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0)

  const location = useLocation()
  const history = useHistory()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  const [textAnswer, setTextAnswer] = useState('')
  const [initialText, setInitialText] = useState('')
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [initialSelectedOptions, setInitialSelectedOptions] = useState<
    string[]
  >([])
  const [fileUrl, setFileUrl] = useState<string>('')
  const [initialFileUrl, setInitialFileUrl] = useState<string>('')

  const currentQuestionIndex = useSelector(selectCurrentQuestionIndex)
  const formSubmissions = useSelector(selectFormSubmission)
  const formId = useSelector(selectCurrentFormId)
  const currentProject = useSelector(selectCurrentProject)
  const hasPrevious = currentQuestionIndex !== undefined
  const questions = useSelector(selectQuestions)
  const hasNext =
    (currentQuestionIndex === undefined && questions.length > 0) ||
    currentQuestionIndex < questions.length - 1
  console.log({ currentQuestionIndex, questions, hasNext })
  const currentQuestion = questions[currentQuestionIndex]

  const dispatch = useDispatch()

  const updateSearchParam = useCallback(
    (paramName: string, value: string) => {
      searchParams.set(paramName, value)
      history.push(`${location.pathname}?${searchParams.toString()}`)
    },
    [history, location.pathname, searchParams],
  )

  const closeModal = useCallback(() => {
    updateSearchParam('active_mission', '')
  }, [updateSearchParam])

  function selfDestruct(ms = 2000) {
    setTimeout(closeModal, ms)
  }

  const { isLoggedIn } = useIsLoggedIn()
  const [fetchFormByTaskIdAndMissionId, { isLoading: isLoadingFormData }] =
    useLazyGetFormByTaskIdAndFormMissionIdQuery()
  const [fetchUserFormSubmissionByTaskId, { isLoading: isLoadingSubmission }] =
    useLazyGetUserFormSubmissionByTaskIdQuery()

  const fetchFormData = useCallback(
    async ({
      taskId,
      formMissionId,
    }: {
      taskId: number
      formMissionId: string
    }) => {
      try {
        const data = await fetchFormByTaskIdAndMissionId({
          taskId,
          formMissionId,
        }).unwrap()

        if (!data) return

        setFormData(data)
        dispatch(setFormId(data.id))
        dispatch(setFormQuestions(data.questions))
      } catch (error) {
        console.error(error)
        toast.error('An error occurred.')
      }
    },
    [dispatch, fetchFormByTaskIdAndMissionId],
  )

  const fetchUserFormSubmission = useCallback(
    async ({ taskId }: { taskId: number }) => {
      try {
        const data = await fetchUserFormSubmissionByTaskId({
          taskId,
        }).unwrap()

        if (!data) return

        dispatch(setFormSubmission(data))
      } catch (error) {
        console.error(error)
        toast.error('An error occurred.')
      }
    },
    [dispatch, fetchUserFormSubmissionByTaskId],
  )

  const formMissionId = subtask.formMissionId
  useEffect(() => {
    if (!isLoadingFormData && !formData) {
      fetchFormData({ taskId, formMissionId })
    }
  }, [fetchFormData, formData, formMissionId, isLoadingFormData, taskId])

  useEffect(() => {
    if (isLoggedIn && !isLoadingSubmission && !!formSubmissions?.length) {
      fetchUserFormSubmission({ taskId })
    }
  }, [
    fetchUserFormSubmission,
    formSubmissions?.length,
    isLoadingSubmission,
    isLoggedIn,
    taskId,
  ])

  const updateFormSubmission = async (missionId: number) => {
    if (!missionId) return
    try {
      const response = await theseus.get(`/forms/${missionId}/submission`)

      if (response.status !== 200) throw new Error('Something went wrong')

      dispatch(setFormSubmission(response.data))
    } catch (error) {
      console.error(error)
      toast.error('Something went wrong')
    }
  }

  const currentQuestionAnswer = formSubmissions?.find(
    (answer) => currentQuestion.id === answer.questionId,
  )

  useEffect(() => {
    if (!formSubmissions?.length) return

    const correctAnswers = formSubmissions.filter((answer) => answer.isCorrect)
    setCorrectAnswersCount(correctAnswers.length)
  }, [formSubmissions])

  useEffect(() => {
    if (!currentQuestionAnswer) {
      setSelectedOptions([])
      setTextAnswer('')
      return
    }

    const { type } = currentQuestion
    if (type === 'multiple_choice') {
      const choiceValues = currentQuestionAnswer?.choiceValues ?? []
      setInitialSelectedOptions(choiceValues)
      setSelectedOptions(choiceValues)
      return
    }

    if (type === 'file_upload') {
      const fileUrl = currentQuestionAnswer?.fileUrl ?? ''
      setInitialFileUrl(fileUrl)
      setFileUrl(fileUrl)
      return
    }

    const textValue = currentQuestionAnswer?.textValue ?? ''
    setInitialText(textValue)
    setTextAnswer(textValue)
  }, [currentQuestion, currentQuestionAnswer])

  const handlePrevious = () => {
    if (!hasPrevious) {
      return
    }
    dispatch(setPreviousQuestion())
  }

  const isGoToPreviousDisabled = !isLoggedIn || isSubmitting || !hasPrevious
  const isGoToNexDisabled =
    !isLoggedIn ||
    isSubmitting ||
    !hasNext ||
    (currentQuestion?.isRequired && !textAnswer && !selectedOptions.length)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const { type, isRequired, id } = currentQuestion

    const isText = type === 'text'
    const isMultipleChoice = type === 'multiple_choice'
    const isFileUpload = type === 'file_upload'

    const isMultipleChoiceAnswerEmpty =
      isMultipleChoice && !selectedOptions.length
    const isTextAnswerEmpty = isText && !textAnswer
    const isFileNotUploaded = isFileUpload && !fileUrl

    if (
      isRequired &&
      (isMultipleChoiceAnswerEmpty || isTextAnswerEmpty || isFileNotUploaded)
    ) {
      toast.error('Please fill out the question')
      setIsSubmitting(false)
      return
    }

    const isTextUnchanged = isText && textAnswer === initialText
    const isFileUrlUnchanged = isFileUpload && fileUrl === initialFileUrl

    if (!isRequired) {
      const submission = formSubmissions?.find(
        (answer) => answer.questionId === id,
      )
      if (!submission) {
        if (
          (isText && textAnswer === '') ||
          (isMultipleChoice && selectedOptions.length === 0)
        ) {
          setSkippedQuestionIds([...skippedQuestionIds, id])
        } else {
          setSkippedQuestionIds(
            skippedQuestionIds.filter((questionId) => questionId !== id),
          )
        }
      }
    }

    if ((isTextUnchanged || isFileUrlUnchanged) && hasNext) {
      handleNext()
      setIsLoading(false)
      setIsSubmitting(false)
      return
    }

    const isMultipleChoiceUnchanged =
      isMultipleChoice &&
      selectedOptions.length === initialSelectedOptions.length

    if (isMultipleChoiceUnchanged) {
      const isSame = selectedOptions.every((option) =>
        initialSelectedOptions.includes(option),
      )

      if (isSame && hasNext) {
        handleNext()
        setIsLoading(false)
        setIsSubmitting(false)
        return
      }
    }

    if (currentQuestion.title.includes('jam.dev')) {
      const jam_regex = /^https:\/\/jam\.dev\/*/
      if (textAnswer && !jam_regex.test(textAnswer)) {
        toast.error('Please provide a valid jam.dev URL')
        setIsSubmitting(false)
        return
      }
    }

    const data = {
      projectId: currentProject.id,
      questionId: id,
      type,
      taskId: subtask.id,
      ...(textAnswer ? { textValue: textAnswer } : {}),
      ...(selectedOptions.length ? { choiceValues: selectedOptions } : {}),
      ...(fileUrl ? { fileUrl } : {}),
    }

    try {
      const response = await theseus.post(`forms/${taskId}/${formId}/submit`, {
        data,
      })

      if (response.status !== 200) throw new Error('Something went wrong')

      const isQuiz = formData.template === 'Quiz'
      if (isQuiz) {
        const { isCorrect } = response.data
        if (isCorrect) {
          setCorrectAnswersCount(correctAnswersCount + 1)
        }
      }

      if (!hasNext) {
        updateTaskSubmissions()
        setIsComplete(true)
        selfDestruct()
        setIsSubmitting(false)
        return
      }
      await updateFormSubmission(taskId)
      setSelectedOptions([])
      setTextAnswer('')
      handleNext()
    } catch (error) {
      console.error(error)
      toast.error('Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNext = async () => {
    if (currentQuestionIndex === undefined) {
      handleStart()
      return
    }
    if (isGoToNexDisabled) return

    const { type, isRequired, id } = currentQuestion

    const isText = type === 'text'
    const isMultipleChoice = type === 'multiple_choice'
    if (!isRequired) {
      const submission = formSubmissions?.find(
        (answer) => answer.questionId === id,
      )
      if (!submission) {
        if (
          (isText && textAnswer === '') ||
          (isMultipleChoice && selectedOptions.length === 0)
        ) {
          setSkippedQuestionIds([...skippedQuestionIds, id])
        } else {
          setSkippedQuestionIds(
            skippedQuestionIds.filter((questionId) => questionId !== id),
          )
        }
      }
    }
    dispatch(setNextQuestion())
  }

  const handleStart = () => {
    setHasStarted(true)
    dispatch(setNextQuestion())
  }

  const numberOfQuestions = formData?.questions.length ?? 0

  const isQuizForm = formData?.template === 'Quiz'
  const hasPreRequisiteLink = formData?.preRequisiteLink

  const rewardAmount = !isQuizForm
    ? reward
    : Math.min(reward * correctAnswersCount, maxReward)

  const handleClickPrequisiteLink = () => {
    if (formData?.preRequisiteLink) {
      window.open(formData.preRequisiteLink, '_blank')
    }
    setHasVisitedPrerequisiteLink(true)
  }

  return (
    <TaskContainer onClick={stopPropagation}>
      <Wrapper direction="column">
        {(!hasStarted || currentQuestionIndex === undefined) && (
          <Box dc ac>
            <ModalText>
              <DescriptionCont className="task-description">
                <CustomMarkdown>{formData?.description}</CustomMarkdown>
              </DescriptionCont>
            </ModalText>
            <FormActions>
              {hasPreRequisiteLink && !hasVisitedPrequisiteLink && (
                <>
                  <span>Click on visit to know more about the quiz</span>
                  <ThemedButton
                    $styleType="primary"
                    onClick={() => handleClickPrequisiteLink()}
                  >
                    Visit to start
                  </ThemedButton>
                </>
              )}
              {(!hasPreRequisiteLink ||
                (hasPreRequisiteLink && hasVisitedPrequisiteLink)) && (
                <>
                  <span>Are you ready?</span>
                  {isLoggedIn ? (
                    <ThemedButton
                      disabled={
                        hasPreRequisiteLink && !hasVisitedPrequisiteLink
                      }
                      $styleType="primary"
                      onClick={handleStart}
                    >
                      Let's go
                    </ThemedButton>
                  ) : (
                    <ConnectWalletButton />
                  )}
                </>
              )}
            </FormActions>
          </Box>
        )}
        {hasStarted && currentQuestionIndex !== undefined && !isComplete && (
          <Question
            textAnswer={textAnswer}
            setTextAnswer={setTextAnswer}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            setFileUrl={setFileUrl}
            fileUrl={fileUrl}
            handleSubmit={handleSubmit}
            isDisabled={isSubmitting}
          />
        )}
        {isComplete && (
          <Box dc ac g={16}>
            <span>Completed!</span>
            {isQuizForm && (
              <Info type="primary">
                You answered {correctAnswersCount} out of {numberOfQuestions}{' '}
                questions correctly
              </Info>
            )}
            <ModalTitle>
              You've earned{' '}
              <Info type="primary">
                {wrapRewardDisplay({
                  value: rewardAmount,
                  isDrops: isDrop,
                  isEthRaise,
                  rewardDisplay: undefined,
                })}
              </Info>
            </ModalTitle>
            <ConfettiExplosion zIndex={10000} />
          </Box>
        )}
        {formData && (
          <ProgressContainer dc g={6}>
            <Box g={8} ac>
              <Box g={8} ac>
                <span>{formData.title}</span>
                <Info type="primary">
                  +
                  {wrapRewardDisplay({
                    value: !isQuizForm ? reward : maxReward,
                    isDrops: isDrop,
                    isEthRaise,
                    rewardDisplay: undefined,
                  })}
                </Info>
              </Box>
              <button
                onClick={handlePrevious}
                disabled={isGoToPreviousDisabled}
                style={{ opacity: isGoToPreviousDisabled ? 0.5 : 1 }}
              >
                <img src="/img/icons/arrow-button-left.svg" alt="previous" />
              </button>
              <button
                onClick={handleNext}
                disabled={isGoToNexDisabled}
                style={{ opacity: isGoToNexDisabled ? 0.5 : 1 }}
              >
                <img src="/img/icons/arrow-button-right.svg" alt="next" />
              </button>
            </Box>
            <ProgressBar
              length={numberOfQuestions}
              filled={
                isComplete
                  ? numberOfQuestions
                  : formSubmissions?.length + skippedQuestionIds.length
              }
            />
            <Info type="primary">TOTAL QUESTIONS {numberOfQuestions}</Info>
          </ProgressContainer>
        )}
      </Wrapper>
    </TaskContainer>
  )
}

export default SharedFormTaskCard
